// src/screens/dashboard/AccessoryHistoryPage.jsx
import React, { useMemo, useState, useEffect } from "react";
import { Search, FileText, CalendarDays, Download } from "lucide-react";
import { ReactComponent as Home } from "../../assets/images/Home.svg";

import PageHeader from "../../components/layout/PageHeader";
import DataTable from "../../components/table/Datatable";
import PaginationFooter from "../../components/table/PaginationFooter";
import DateRangeButton from "../../components/inputs/DateRangeButton";

import PdfModal from "../../components/modals/PdfModal";
import { createHttpClient } from "../../api/client";
import config from "../../config/env";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function compareText(a, b) {
  const A = String(a ?? "").toLowerCase();
  const B = String(b ?? "").toLowerCase();
  if (A < B) return -1;
  if (A > B) return 1;
  return 0;
}

function asText(v) {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return v.map(asText).filter(Boolean).join(", ");
  if (typeof v === "object") return v.name ?? v.label ?? v.title ?? v.value ?? (v.id != null ? String(v.id) : "");
  return String(v);
}

function joinImgUrl(base, path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const b = String(base || "").replace(/\/+$/, "");
  const p = String(path || "").replace(/^\/+/, "");
  return b && p ? `${b}/${p}` : "";
}

function formatThaiDateFromISODateTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const y = d.getFullYear() + 543;
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${day}/${m}/${y} ${hh}:${mi}:${ss}`;
}

function mapTransactionRow(it) {
  const productAccessory = it?.product_accessory || {};
  const accessoryCategoryName = asText(productAccessory?.accessory_category?.name || productAccessory?.accessory_category);
  const itemName = asText(productAccessory?.name);
  const item = [accessoryCategoryName, itemName].filter(Boolean).join(" - ") || itemName || accessoryCategoryName || "-";
  const qtyChange = Number(it?.qty_change ?? 0);
  const changeText = Number.isFinite(qtyChange) ? `${qtyChange > 0 ? "+" : ""}${qtyChange}` : "-";
  const userName = asText(it?.user?.username || it?.user?.full_name || it?.user?.email || it?.user);
  const rawBillNo = asText(it?.bill_number).trim();
  const billNo = rawBillNo ? (/^bp/i.test(rawBillNo) ? rawBillNo : `BP${rawBillNo}`) : "-";
  const pdfUrl = joinImgUrl(config.apiImg, asText(it?.pdf_path));

  return {
    id: it?.id || it?.documentId || billNo,
    datetime: formatThaiDateFromISODateTime(it?.date || it?.createdAt || it?.created_at),
    item,
    by: userName || "-",
    change: changeText,
    note: asText(it?.note) || "-",
    billNo,
    pdfUrl,
  };
}

export default function AccessoryHistoryPage() {
  const http = useMemo(() => createHttpClient(), []);
  const [query, setQuery] = useState("");
  const [range, setRange] = useState([null, null]); // [startDate, endDate]
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ✅ sort state (เพิ่ม)
  const [sortKey, setSortKey] = useState(""); // "item"
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"
  const onSort = (key) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir("asc");
      return key;
    });
  };

  // pdf modal state
  const [pdfOpen, setPdfOpen] = useState(false);
  const [activeBill, setActiveBill] = useState(null); // {billNo, pdfUrl}

  const headerTitle = (label, key) => (
    <span className={classNames("inline-flex min-w-0 items-center gap-2", sortKey === key && "text-[#047DED]")}>
      <span className="block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{label}</span>
    </span>
  );

  const columns = useMemo(
    () => [
      { key: "datetime", title: "วันที่และเวลา", className: "text-slate-700", headerClassName: "w-[150px]", pl: "pl-6" },

      // ✅ รายการ: กด sort ได้
      {
        key: "item",
        title: "รายการ",
        className: "font-semibold",
        headerClassName: "w-[350px]",
        sortable: true,
        sortKey: "item",
      },

      { key: "by", title: "เพิ่มโดย", headerClassName: "w-[100px]" },
      { key: "change", title: "การเปลี่ยนแปลง", headerClassName: "w-[120px]" },
      { key: "note", title: headerTitle("หมายเหตุ", "note"), headerClassName: "w-[300px] truncate whitespace-nowrap", render: (r) => <div className="truncate">{r.note || "-"}</div> },
      { key: "billNo", title: "เลขบิล", headerClassName: "w-[100px]" },
      {
        key: "manage",
        title: "จัดการ",
        pl: "pl-6",
        headerClassName: "w-[220px]",
        render: (r) =>
          r.pdfUrl ? (
            <div className="inline-flex items-center gap-2">
              <a
                href={r.pdfUrl}
                target="_blank"
                rel="noreferrer"
                download
                className={classNames(
                  "inline-flex items-center gap-2 rounded-xl bg-blue-50 px-4 py-2",
                  "text-sm font-semibold text-[#047DED] active:scale-[0.98] hover:bg-[#047DED] hover:text-white"
                )}
              >
                <FileText size={16} />
                บิล
              </a>
              {/* <a
                href={r.pdfUrl}
                target="_blank"
                rel="noreferrer"
                download
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <Download size={15} />
                ดาวน์โหลด
              </a> */}
            </div>
          ) : (
            <span className="text-slate-300">-</span>
          ),
      },
    ],
    []
  );

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setError("");

        const [start, end] = Array.isArray(range) ? range : [null, null];
        const s = start ? new Date(start) : null;
        const e = end ? new Date(end) : null;
        if (s) s.setHours(0, 0, 0, 0);
        if (e) e.setHours(23, 59, 59, 999);

        const res = await http.get("/product-accessory-transactions", {
          params: {
            "sort[0]": "date:desc",
            "pagination[page]": 1,
            "pagination[pageSize]": 2000,
            "populate[product_accessory][populate][accessory_category]": true,
            "populate[user]": true,
            ...(s && e ? { "filters[date][$gte]": s.toISOString() } : {}),
            ...(s && e ? { "filters[date][$lte]": e.toISOString() } : {}),
          },
          signal: ac.signal,
        });

        const list = res?.data?.data ?? [];
        const mapped = (Array.isArray(list) ? list : []).map(mapTransactionRow);

        if (!alive) return;
        setRows(mapped);
      } catch (e) {
        if (e?.name === "CanceledError" || e?.name === "AbortError") return;
        console.error(e);
        if (!alive) return;
        setError("โหลดข้อมูลไม่สำเร็จ");
        setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [http, range?.[0], range?.[1]]);

  // filter rows
  const filtered = useMemo(() => {
    let next = [...rows];

    if (query.trim()) {
      const q = query.trim().toLowerCase();
      next = next.filter((r) =>
        [r.datetime, r.item, r.by, r.change, r.note, r.billNo].some((x) =>
          String(x || "").toLowerCase().includes(q)
        )
      );
    }

    return next;
  }, [rows, query]);

  // ✅ sort หลัง filter (เพิ่ม)
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const next = [...filtered].sort((a, b) => {
      const c = compareText(a?.[sortKey], b?.[sortKey]);
      return sortDir === "asc" ? c : -c;
    });
    return next;
  }, [filtered, sortKey, sortDir]);

  // pagination
  const totalItems = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.max(1, Math.min(page, totalPages));

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safePage]);

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, safePage, pageSize]);

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, range]);

  return (
    <div className="space-y-4 p-4 bg-white min-h-full">
      <PageHeader
        icon={Home}
        breadcrumb="หน้าหลัก"
        title="ประวัตินำเข้า/ขายอุปกรณ์เสริม"
        subtitle=""
        showExport={false}
        exportPath="/export/accessory-history"
      />

      {/* toolbar */}
      <div className="rounded-2xl bg-[#F5F5F5] p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          {/* search */}
          <div className="w-full md:max-w-[520px]">
            <div className="flex items-center gap-3 rounded-xl border hover:border-2 border-[#1D161633] bg-white px-4 py-3">
              <Search size={18} className="text-[#1D1616B2]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full bg-transparent text-sm outline-none placeholder:text-[#1D161680]"
                placeholder="ค้นหารายการ..."
              />
            </div>
          </div>

          {/* right */}
          <div className="flex w-full flex-wrap items-center justify-start gap-2 md:w-auto md:justify-end">
            <DateRangeButton
              mode="single"
              range={range}
              setRange={setRange}
              leftIcon={<CalendarDays size={16} />}
              label="ช่วงเวลา"
            />
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={pageRows}
        rowHeight="h-16"
        headerBg="bg-slate-50"
        striped={false}
        tableMinWidth="min-w-[1100px]"

        // ✅ ส่ง sort เข้าไป
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={onSort}
      />

      {loading && <div className="text-sm text-slate-500">กำลังโหลดข้อมูล...</div>}
      {!loading && error && <div className="text-sm text-red-500">{error}</div>}

      <PaginationFooter
        totalItems={totalItems}
        page={safePage}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
        pageSizeOptions={[10, 20, 50]}
      />

      <PdfModal
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        title={activeBill?.billNo ? `บิล ${activeBill.billNo}` : "บิล"}
        pdfUrl={activeBill?.pdfUrl}
      />
    </div>
  );
}
